// Reason: Handles user login and registration (email/password) for authentication.
// Enhanced: Animated, aesthetic login card with glassmorphism, animated icon, and Framer Motion effects.

import React, { useState } from "react";
import { motion } from "framer-motion";

const iconVariants = {
  initial: { scale: 0.8, rotate: -10, opacity: 0 },
  animate: { scale: 1, rotate: 0, opacity: 1, transition: { type: "spring", stiffness: 200, damping: 12 } },
  hover: { scale: 1.1, rotate: 8, transition: { type: "spring", stiffness: 300 } }
};

const cardVariants = {
  initial: { y: 40, opacity: 0, scale: 0.95 },
  animate: { y: 0, opacity: 1, scale: 1, transition: { type: "spring", stiffness: 120, damping: 16 } }
};

const buttonVariants = {
  hover: { scale: 1.04, boxShadow: "0px 4px 24px 0px rgba(59,130,246,0.15)" },
  tap: { scale: 0.97 }
};

const AuthForm = ({ onAuth, loading }) => {
  const [mode, setMode] = useState("login"); // "login" or "register"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [confirmationSent, setConfirmationSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const result = await onAuth({ email, password, mode });
      // If registering, show confirmation message and do not proceed to login
      if (mode === "register" && result && result.user && !result.user.confirmed_at) {
        setConfirmationSent(true);
      }
    } catch (err) {
      setError(err.message || "Authentication failed");
    }
  };

  if (confirmationSent) {
    return (
      <div className="relative w-full flex flex-col items-center">
        <motion.div
          className="relative z-10 max-w-sm w-full mx-auto p-8 rounded-2xl shadow-2xl bg-white/70 dark:bg-gray-900/60 backdrop-blur-lg border border-white/30 dark:border-gray-700/40"
          variants={cardVariants}
          initial="initial"
          animate="animate"
        >
          <motion.div
            className="flex justify-center mb-6"
            variants={iconVariants}
            initial="initial"
            animate="animate"
            whileHover="hover"
          >
            <svg width="56" height="56" viewBox="0 0 56 56" fill="none">
              <circle cx="28" cy="28" r="28" fill="url(#paint0_linear)" />
              <path d="M18 32C18 27.5817 21.5817 24 26 24H30C34.4183 24 38 27.5817 38 32V36C38 37.1046 37.1046 38 36 38H20C18.8954 38 18 37.1046 18 36V32Z" fill="#fff" />
              <circle cx="28" cy="20" r="6" fill="#fff" />
              <defs>
                <linearGradient id="paint0_linear" x1="0" y1="0" x2="56" y2="56" gradientUnits="userSpaceOnUse">
                  <stop stopColor="#6366F1"/>
                  <stop offset="1" stopColor="#EC4899"/>
                </linearGradient>
              </defs>
            </svg>
          </motion.div>
          <h2 className="text-2xl font-extrabold mb-4 text-center text-gray-800 dark:text-white tracking-tight drop-shadow">
            Confirm Your Email
          </h2>
          <div className="text-center text-gray-700 dark:text-gray-200 mb-4">
            We've sent a confirmation link to <span className="font-semibold">{email}</span>.<br />
            Please check your inbox and confirm your email to activate your account.
          </div>
          <button
            className="w-full mt-4 py-2 rounded-lg font-semibold bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg"
            onClick={() => setConfirmationSent(false)}
          >
            Back to Login
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="relative w-full flex flex-col items-center">
      {/* Animated Gradient Background */}
      <div
        className="absolute inset-0 z-0 pointer-events-none"
        aria-hidden="true"
      >
        <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-[480px] h-[480px] rounded-full bg-gradient-to-tr from-blue-400 via-indigo-400 to-pink-400 opacity-40 blur-3xl animate-pulse" />
        <div className="absolute bottom-0 right-0 w-72 h-72 rounded-full bg-gradient-to-br from-pink-400 via-blue-400 to-indigo-400 opacity-30 blur-2xl animate-pulse" />
      </div>

      {/* Animated Card */}
      <motion.div
        className="relative z-10 max-w-sm w-full mx-auto p-8 rounded-2xl shadow-2xl bg-white/70 dark:bg-gray-900/60 backdrop-blur-lg border border-white/30 dark:border-gray-700/40"
        variants={cardVariants}
        initial="initial"
        animate="animate"
      >
        {/* Animated Icon/Logo */}
        <motion.div
          className="flex justify-center mb-6"
          variants={iconVariants}
          initial="initial"
          animate="animate"
          whileHover="hover"
        >
          <svg width="56" height="56" viewBox="0 0 56 56" fill="none">
            <circle cx="28" cy="28" r="28" fill="url(#paint0_linear)" />
            <path d="M18 32C18 27.5817 21.5817 24 26 24H30C34.4183 24 38 27.5817 38 32V36C38 37.1046 37.1046 38 36 38H20C18.8954 38 18 37.1046 18 36V32Z" fill="#fff" />
            <circle cx="28" cy="20" r="6" fill="#fff" />
            <defs>
              <linearGradient id="paint0_linear" x1="0" y1="0" x2="56" y2="56" gradientUnits="userSpaceOnUse">
                <stop stopColor="#6366F1"/>
                <stop offset="1" stopColor="#EC4899"/>
              </linearGradient>
            </defs>
          </svg>
        </motion.div>
        <h2 className="text-2xl font-extrabold mb-4 text-center text-gray-800 dark:text-white tracking-tight drop-shadow">
          {mode === "login" ? "Welcome Back" : "Create Account"}
        </h2>
        <form onSubmit={handleSubmit} className="space-y-5">
          <input
            type="email"
            className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white/80 dark:bg-gray-800/70 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-all duration-200 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 shadow-sm"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            aria-label="Email"
            autoComplete="email"
          />
          <input
            type="password"
            className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white/80 dark:bg-gray-800/70 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 transition-all duration-200 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 shadow-sm"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            aria-label="Password"
            autoComplete={mode === "login" ? "current-password" : "new-password"}
          />
          <motion.button
            type="submit"
            className="w-full py-2 rounded-lg font-semibold bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-indigo-600 hover:to-pink-500 text-white shadow-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-pink-400 focus:ring-offset-2"
            variants={buttonVariants}
            whileHover="hover"
            whileTap="tap"
            disabled={loading}
          >
            {loading
              ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin h-5 w-5 mr-2 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Processing...
                </span>
              )
              : mode === "login"
              ? "Sign In"
              : "Register"}
          </motion.button>
        </form>
        <motion.button
          className="mt-5 text-blue-600 dark:text-pink-400 underline w-full text-sm font-medium transition-colors duration-200 hover:text-indigo-600 dark:hover:text-pink-300"
          onClick={() => setMode(mode === "login" ? "register" : "login")}
          type="button"
          whileHover={{ scale: 1.05, color: "#6366F1" }}
        >
          {mode === "login"
            ? "Don't have an account? Register"
            : "Already have an account? Sign In"}
        </motion.button>
        {error && (
          <div className="mt-3 text-red-600 text-sm text-center">{error}</div>
        )}
      </motion.div>
    </div>
  );
};

export default AuthForm;
